import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #availabilityObserver;

  /** @type {number} */
  #availabilitySortFrame = 0;

  /** @type {boolean} */
  #isSortingAvailability = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#observeGridAvailability();
    this.#scheduleAvailabilitySort();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#availabilityObserver?.disconnect();
    if (this.#availabilitySortFrame) cancelAnimationFrame(this.#availabilitySortFrame);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
    this.#scheduleAvailabilitySort();
  };

  #observeGridAvailability() {
    this.#availabilityObserver?.disconnect();

    this.#availabilityObserver = new MutationObserver(() => {
      if (this.#isSortingAvailability) return;
      this.#scheduleAvailabilitySort();
    });

    this.#availabilityObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-last-page', 'data-product-available'],
      childList: true,
      subtree: true,
    });
  }

  #scheduleAvailabilitySort() {
    if (this.#availabilitySortFrame) cancelAnimationFrame(this.#availabilitySortFrame);

    this.#availabilitySortFrame = requestAnimationFrame(() => {
      this.#availabilitySortFrame = 0;
      this.#sortAvailableProductsFirst();
    });
  }

  /**
   * Availability sorting is duplicated here because this public asset is loaded on collection pages.
   * It keeps filtered and infinite-scroll results from leading with sold-out products.
   */
  #sortAvailableProductsFirst() {
    const { grid } = this.refs;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    if (cards.length < 2) return;

    const hasUnavailableCards = cards.some((card) => this.#isUnavailableGridCard(card));
    if (!hasUnavailableCards) return;

    const originalOrder = new Map(cards.map((card, index) => [card, index]));
    const sortedCards = [...cards].sort((cardA, cardB) => {
      const unavailableA = this.#isUnavailableGridCard(cardA);
      const unavailableB = this.#isUnavailableGridCard(cardB);
      if (unavailableA !== unavailableB) return unavailableA ? 1 : -1;

      return (originalOrder.get(cardA) ?? 0) - (originalOrder.get(cardB) ?? 0);
    });

    const changed = sortedCards.some((card, index) => card !== cards[index]);
    if (!changed) return;

    this.#isSortingAvailability = true;
    const fragment = document.createDocumentFragment();
    for (const card of sortedCards) {
      fragment.append(card);
    }
    grid.append(fragment);
    this.#isSortingAvailability = false;
  }

  /**
   * @param {Element} card
   * @returns {boolean}
   */
  #isUnavailableGridCard(card) {
    const availableValue = card.getAttribute('data-product-available');
    return availableValue === 'false' || card.classList.contains('product-grid__item--sold-out');
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

const hotPicksSyncVersion = '20260625-available-grid-sync-v1';

const normalizeBestSellerLinks = () => {
  for (const link of document.querySelectorAll('a[href*="/collections/all"][href*="sort_by=best-selling"]')) {
    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      if (!/\/collections\/all\/?$/.test(url.pathname)) continue;
      if (url.searchParams.get('sort_by') !== 'best-selling') continue;

      if (!url.searchParams.get('page')) url.searchParams.set('page', '1');
      url.searchParams.delete('view');
      link.setAttribute('href', `${url.pathname}${url.search}`);
    } catch (_error) {
      // Keep the original link if it cannot be parsed.
    }
  }
};

const normalizeBestSellerLanguageLinks = () => {
  const currentUrl = new URL(window.location.href);
  const normalizedPath = currentUrl.pathname.replace(/^\/en(?=\/|$)/, '') || '/';
  const isBestSellingAll =
    /\/collections\/all\/?$/.test(normalizedPath) && currentUrl.searchParams.get('sort_by') === 'best-selling';

  if (!isBestSellingAll) return;

  const page = currentUrl.searchParams.get('page') || '1';

  for (const link of document.querySelectorAll('a[data-lang][href]')) {
    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      const targetPath = url.pathname.replace(/^\/en(?=\/|$)/, '') || '/';
      if (!/\/collections\/all\/?$/.test(targetPath)) continue;

      url.searchParams.set('sort_by', 'best-selling');
      url.searchParams.set('page', page);
      url.searchParams.delete('view');
      link.setAttribute('href', `${url.pathname}${url.search}`);
    } catch (_error) {
      // Keep the original language link if it cannot be parsed.
    }
  }
};

const injectCollectionHotPicksFromFreshSection = async () => {
  const url = new URL(window.location.href);
  const isBestSellingAll =
    /\/collections\/all\/?$/.test(url.pathname) && url.searchParams.get('sort_by') === 'best-selling';

  const existingHotPicks = document.querySelector('[data-collection-hot-picks]');
  const isFreshHotPicks = existingHotPicks?.dataset.syncVersion === hotPicksSyncVersion;

  if (!isBestSellingAll || isFreshHotPicks) return;

  const headingSection = document.getElementById('shopify-section-section');
  if (!headingSection && !existingHotPicks) return;

  try {
    const response = await fetch(
      `${window.location.pathname}?sections=section&sort_by=best-selling&_gc_hot=${Date.now()}`,
      { credentials: 'same-origin' }
    );
    if (!response.ok) return;

    const payload = await response.json();
    const html = payload?.section;
    if (!html) return;

    const parsed = document.createElement('div');
    parsed.innerHTML = html;

    const hotPicks = parsed.querySelector('[data-collection-hot-picks]');
    if (!hotPicks) return;

    const style = parsed.querySelector('style');
    if (style) (existingHotPicks || headingSection).before(style);

    if (existingHotPicks) {
      existingHotPicks.replaceWith(hotPicks);
    } else {
      headingSection.before(hotPicks);
    }

    for (const script of parsed.querySelectorAll('script')) {
      const executable = document.createElement('script');
      executable.textContent = script.textContent;
      document.body.appendChild(executable);
      executable.remove();
    }
  } catch (_error) {
    // Non-critical storefront enhancement; keep the product grid usable if section fetch fails.
  }
};

normalizeBestSellerLinks();
normalizeBestSellerLanguageLinks();
injectCollectionHotPicksFromFreshSection();
